// The mates' weekly board on Activity: you and the mates who switched it on
// (Settings › Badges), Monday to Sunday. Sessions ranks how far each of you is
// towards your own target; the row and ride tabs rank this week's best against
// each person's usual on the same machine. Never raw speed. Rules:
// api/lib/board.js.

import React, { useEffect, useState } from "react";
import * as api from "../../services/api";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import { useSocial } from "../../contexts/SocialContext";
import { Group } from "../profile/ProfileParts";
import Avatar from "./Avatar";
import { cn } from "../../lib/utils";
import { useBadgesShown } from "./Badges";
import { effortLine, liveTabs, sessionsBar, shortTab, signedDelta, signedPct, weekLabel } from "../../utils/board";

const rowClass = "grid grid-cols-[1.25rem_2rem_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2.5";

/** Rank, face, name: the start of every row. */
const Who = ({ r }) => (
  <>
    <span className="text-center font-mono text-sm font-bold tabular-nums text-muted-foreground">{r.rank ?? "–"}</span>
    <Avatar name={r.name} photo={r.photo_url} />
  </>
);

function SessionsRows({ rows }) {
  return rows.map((r) => {
    const { fill, full } = sessionsBar(r.pct);
    return (
      <div key={r.userId} className={cn(rowClass, r.me && "bg-muted/30")}>
        <Who r={r} />
        <span className="grid min-w-0 gap-1.5">
          <span className="truncate text-sm font-medium">{r.me ? "You" : r.name}</span>
          <span className="relative h-1.5 rounded-full bg-muted">
            <span
              className={cn("absolute inset-y-0 left-0 rounded-full", r.pct >= 100 ? "bg-green-500" : "bg-amber-400")}
              style={{ width: `${fill}%` }}
            />
            <span aria-hidden="true" className="absolute -bottom-0.5 -top-0.5 w-px bg-muted-foreground" style={{ left: `${full}%` }} />
          </span>
        </span>
        <span className="text-right">
          <span className="block text-sm font-semibold tabular-nums">{r.pct}%</span>
          <span className="block text-[11px] text-muted-foreground tabular-nums">
            {r.days} of {r.target} {r.target === 1 ? "day" : "days"}
          </span>
        </span>
      </div>
    );
  });
}

function EffortRows({ rows, tab }) {
  return rows.map((r) => {
    const better = r.improvement != null && r.improvement > 0;
    return (
      <div key={r.userId} className={cn(rowClass, r.me && "bg-muted/30")}>
        <Who r={r} />
        <span className="grid min-w-0 gap-0.5">
          <span className="truncate text-sm font-medium">{r.me ? "You" : r.name}</span>
          <span className="truncate text-xs text-muted-foreground tabular-nums">{effortLine(r, tab)}</span>
        </span>
        {r.improvement != null ? (
          <span className="text-right">
            <span className={cn("block text-sm font-semibold tabular-nums", better ? "text-green-400" : "text-muted-foreground")}>
              {signedPct(r.improvement, tab.lower)}
            </span>
            <span className="block text-[11px] text-muted-foreground tabular-nums">{signedDelta(r.delta, tab.unit)}</span>
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">–</span>
        )}
      </div>
    );
  });
}

export default function MatesBoardCard() {
  const shown = useBadgesShown();
  const [ws] = useWorkoutSettings();
  const onBoard = ws.showOnBoard === true;
  const { badgesVersion } = useSocial();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sessions");

  useEffect(() => {
    if (!shown || !onBoard) return undefined;
    let cancelled = false;
    api
      .getMatesBoard()
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setData(null));
    return () => {
      cancelled = true;
    };
  }, [shown, onBoard, badgesVersion]);

  if (!shown || !onBoard || !data?.optedIn) return null;
  if (!data.eligible) {
    return (
      <Group title="Mates' board">
        <p className="px-3 py-3 text-[13px] leading-snug text-muted-foreground">
          Your board appears once a mate switches it on too, in Settings › Badges.
        </p>
      </Group>
    );
  }

  const efforts = liveTabs(data.tabs, data.efforts);
  const tabs = [{ key: "sessions", title: "Sessions" }, ...efforts];
  const current = tabs.find((t) => t.key === tab) || tabs[0];

  return (
    <Group title="Mates' board" note={`Since ${weekLabel(data.weekStart)} · resets Monday`}>
      {tabs.length > 1 && (
        <div className="px-3 py-2.5">
          <div role="tablist" aria-label="Board" className="flex gap-1 rounded-lg bg-muted/50 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={t.key === current.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  "min-w-0 flex-1 truncate rounded-md px-1.5 py-1.5 text-xs font-semibold transition-colors",
                  t.key === current.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {shortTab(t)}
              </button>
            ))}
          </div>
        </div>
      )}
      {current.key === "sessions" ? (
        <SessionsRows rows={data.sessions || []} />
      ) : (
        <EffortRows rows={data.efforts?.[current.key] || []} tab={current} />
      )}
      <p className="px-3 py-2.5 text-xs leading-snug text-muted-foreground">
        {current.key === "sessions"
          ? "Days trained against your own weekly target, up to 150%."
          : "This week's best against your usual: the middle of your last 5 on the same machine. Raw speed and watts are never ranked."}
      </p>
    </Group>
  );
}
